import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { VendorPortal } from '@/vendor/VendorPortal';
import { LoginPage } from '@/pages/LoginPage';
import { AcceptInvitePage } from '@/pages/AcceptInvitePage';
import { CallbackPage } from '@/pages/CallbackPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { RequireAdmin } from '@/components/RequireAdmin';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AppLayout } from '@/components/AppLayout';
import { AssuranceTabs, InventoryTabs, InsightsTabs, ServiceDeskTabs, ConfigurationTabs } from '@/components/SectionTabs';
import { DashboardsPage } from '@/pages/DashboardsPage';
import { EdgesPage } from '@/pages/EdgesPage';
import { CamerasPage } from '@/pages/CamerasPage';
import { TicketsPage } from '@/pages/TicketsPage';
import { VendorsPage } from '@/pages/VendorsPage';
import { ContractsPage } from '@/pages/ContractsPage';
import { ContractDetailPage } from '@/pages/ContractDetailPage';
import { AssetsPage } from '@/pages/AssetsPage';
import { SitesPage } from '@/pages/SitesPage';
import { TeamPage } from '@/pages/TeamPage';
import { AnalyticsPage } from '@/pages/AnalyticsPage';
import { ReportsPage } from '@/pages/ReportsPage';
import { SettingsPage } from '@/pages/SettingsPage';

// New spec pages — lazy so they don't bloat the initial bundle.
const DevicesPage = lazy(() => import('@/pages/DevicesPage').then((m) => ({ default: m.DevicesPage })));
const DeviceDetailPage = lazy(() => import('@/pages/DeviceDetailPage').then((m) => ({ default: m.DeviceDetailPage })));
const MapPage = lazy(() => import('@/pages/MapPage').then((m) => ({ default: m.MapPage })));
// Issues = the single operations queue (merged Alarm Center + Incident Mgmt).
// /events, /alarms, /incidents all redirect to /issues (see routes below).
const EventsPage = lazy(() => import('@/pages/EventsPage').then((m) => ({ default: m.EventsPage })));
const RecordingPage = lazy(() => import('@/pages/RecordingPage').then((m) => ({ default: m.RecordingPage })));
const FirmwarePage = lazy(() => import('@/pages/FirmwarePage').then((m) => ({ default: m.FirmwarePage })));
const TopologyPage = lazy(() => import('@/pages/TopologyPage').then((m) => ({ default: m.TopologyPage })));
const AiHealthPage = lazy(() => import('@/pages/AiHealthPage').then((m) => ({ default: m.AiHealthPage })));
const SlaPage = lazy(() => import('@/pages/SlaPage').then((m) => ({ default: m.SlaPage })));
const EscalationsPage = lazy(() => import('@/pages/EscalationsPage').then((m) => ({ default: m.EscalationsPage })));
const VendorPerformancePage = lazy(() => import('@/pages/VendorPerformancePage').then((m) => ({ default: m.VendorPerformancePage })));
const ContractDashboardPage = lazy(() => import('@/pages/ContractDashboardPage').then((m) => ({ default: m.ContractDashboardPage })));
const TicketDetailPage = lazy(() => import('@/pages/TicketDetailPage').then((m) => ({ default: m.TicketDetailPage })));
const AutomationPage = lazy(() => import('@/pages/AutomationPage').then((m) => ({ default: m.AutomationPage })));
// Video Assurance (§7.8–7.9)
const VideoAssuranceDashboardPage = lazy(() => import('@/pages/VideoAssuranceDashboardPage').then((m) => ({ default: m.VideoAssuranceDashboardPage })));
const CameraHealthPage = lazy(() => import('@/pages/CameraHealthPage').then((m) => ({ default: m.CameraHealthPage })));
const RecordingVerificationPage = lazy(() => import('@/pages/RecordingVerificationPage').then((m) => ({ default: m.RecordingVerificationPage })));
const BlurAnalysisPage = lazy(() => import('@/pages/BlurAnalysisPage').then((m) => ({ default: m.BlurAnalysisPage })));
const RetentionCompliancePage = lazy(() => import('@/pages/RetentionCompliancePage').then((m) => ({ default: m.RetentionCompliancePage })));
const PlaybackVerificationPage = lazy(() => import('@/pages/PlaybackVerificationPage').then((m) => ({ default: m.PlaybackVerificationPage })));
const NocWallboardPage = lazy(() => import('@/pages/NocWallboardPage').then((m) => ({ default: m.NocWallboardPage })));
// Asset QR support: detail page (QR landing target) + printable label sheet.
const AssetDetailPage = lazy(() => import('@/pages/AssetDetailPage').then((m) => ({ default: m.AssetDetailPage })));
const AssetLabelsPrintPage = lazy(() => import('@/pages/AssetLabelsPrintPage').then((m) => ({ default: m.AssetLabelsPrintPage })));

const L = (el: React.ReactNode) => (
  <Suspense fallback={<div className="grid h-64 place-items-center text-muted">Loading…</div>}>{el}</Suspense>
);

export function App() {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();

  // Vendor portal is a fully self-contained app (own login + token + shell),
  // independent of the operator session. Intercept /vendor/* before anything
  // else so a vendor never hits the operator auth gate.
  if (pathname === '/vendor' || pathname.startsWith('/vendor/')) {
    return <VendorPortal />;
  }

  // Accept-invite / set-password lives outside the operator auth gate — the user
  // following an invite email has no session yet (and may already have a stale
  // one). It self-validates the token, so it's safe pre-auth.
  if (pathname === '/accept-invite') {
    return <AcceptInvitePage />;
  }

  // OIDC sign-in callback (AUTH_FLOW=oidc): completes the Logto PKCE redirect and
  // exchanges the token for an IOSentra session. Must run BEFORE the auth gate —
  // the user isn't signed in yet when they land here.
  if (pathname === '/callback') {
    return <CallbackPage />;
  }

  if (loading) {
    return <div className="grid h-full place-items-center text-muted">Loading…</div>;
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // NOC Wallboard is a full-bleed kiosk — rendered outside AppLayout (no
  // sidebar/topbar) so it fills the screen and pairs with browser fullscreen.
  if (pathname === '/noc') {
    return L(<NocWallboardPage />);
  }

  // Label print sheet renders outside AppLayout so the printed page carries no
  // sidebar/topbar chrome (same mechanism as the NOC wallboard above).
  if (pathname === '/assets/labels') {
    return L(<AssetLabelsPrintPage />);
  }

  return (
    <AppLayout>
      {/* Per-page boundary: a single page crash shows a recovery UI without
          taking down the shell/nav (audit ERR-01). key=pathname resets it on nav. */}
      <ErrorBoundary variant="inline" label="page" key={pathname}>
      <Routes>
        {/* Authenticated users who land on /login (post-login, bookmark, back
            button) would otherwise hit the catch-all 404 — which renders OUTSIDE
            AppLayout, trapping them with no nav/sign-out. Send them home. */}
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/" element={<DashboardsPage />} />
        <Route path="/cameras" element={<CamerasPage />} />
        <Route path="/edges" element={<RequireAdmin><ConfigurationTabs><EdgesPage /></ConfigurationTabs></RequireAdmin>} />
        {/* Monitoring */}
        <Route path="/map" element={L(<MapPage />)} />
        <Route path="/topology" element={<InventoryTabs>{L(<TopologyPage />)}</InventoryTabs>} />
        <Route path="/ai-health" element={<AssuranceTabs>{L(<AiHealthPage />)}</AssuranceTabs>} />
        <Route path="/recording" element={<AssuranceTabs>{L(<RecordingPage />)}</AssuranceTabs>} />
        {/* Operations — Events merges the former Alarm Center + Incidents. The
            old paths redirect (preserving deep-links) into the right tab. */}
        <Route path="/issues" element={L(<EventsPage />)} />
        {/* Legacy routes → the unified Issues queue (audit: Alarm+Incident merge). */}
        <Route path="/events" element={<Navigate to="/issues" replace />} />
        <Route path="/alarms" element={<Navigate to="/issues" replace />} />
        <Route path="/incidents" element={<Navigate to="/issues" replace />} />
        <Route path="/tickets" element={<ServiceDeskTabs><TicketsPage /></ServiceDeskTabs>} />
        <Route path="/tickets/:id" element={L(<TicketDetailPage />)} />
        <Route path="/sla" element={<ServiceDeskTabs>{L(<SlaPage />)}</ServiceDeskTabs>} />
        <Route path="/escalations" element={<ServiceDeskTabs>{L(<EscalationsPage />)}</ServiceDeskTabs>} />
        <Route path="/vendor-performance" element={<ServiceDeskTabs>{L(<VendorPerformancePage />)}</ServiceDeskTabs>} />
        <Route path="/devices" element={<InventoryTabs>{L(<DevicesPage />)}</InventoryTabs>} />
        <Route path="/devices/:id" element={L(<DeviceDetailPage />)} />
        <Route path="/firmware" element={<InventoryTabs>{L(<FirmwarePage />)}</InventoryTabs>} />
        <Route path="/sites" element={<InventoryTabs><SitesPage /></InventoryTabs>} />
        {/* Video Assurance (§7.8–7.9) */}
        <Route path="/video-assurance" element={<AssuranceTabs>{L(<VideoAssuranceDashboardPage />)}</AssuranceTabs>} />
        <Route path="/video-assurance/cameras" element={<AssuranceTabs>{L(<CameraHealthPage />)}</AssuranceTabs>} />
        <Route path="/video-assurance/recording-verification" element={<AssuranceTabs>{L(<RecordingVerificationPage />)}</AssuranceTabs>} />
        {/* Image Quality merged into the generic analyzer; redirect old bookmarks. */}
        <Route path="/video-assurance/image-quality" element={<Navigate to="/video-assurance/blur" replace />} />
        <Route path="/video-assurance/blur" element={<AssuranceTabs>{L(<BlurAnalysisPage />)}</AssuranceTabs>} />
        <Route path="/video-assurance/retention" element={<AssuranceTabs>{L(<RetentionCompliancePage />)}</AssuranceTabs>} />
        <Route path="/video-assurance/playback" element={<AssuranceTabs>{L(<PlaybackVerificationPage />)}</AssuranceTabs>} />
        {/* Management */}
        <Route path="/vendors" element={<ServiceDeskTabs><VendorsPage /></ServiceDeskTabs>} />
        <Route path="/contracts" element={<ServiceDeskTabs><ContractsPage /></ServiceDeskTabs>} />
        <Route path="/contract-dashboard" element={<ServiceDeskTabs>{L(<ContractDashboardPage />)}</ServiceDeskTabs>} />
        <Route path="/contracts/:id" element={<ContractDetailPage />} />
        <Route path="/assets" element={<InventoryTabs><AssetsPage /></InventoryTabs>} />
        <Route path="/assets/:id" element={L(<AssetDetailPage />)} />
        <Route path="/team" element={<RequireAdmin><ConfigurationTabs><TeamPage /></ConfigurationTabs></RequireAdmin>} />
        {/* Insights */}
        <Route path="/analytics" element={<InsightsTabs><AnalyticsPage /></InsightsTabs>} />
        <Route path="/reports" element={<InsightsTabs><ReportsPage /></InsightsTabs>} />
        <Route path="/provider" element={<DashboardsPage />} />
        <Route path="/executive" element={<DashboardsPage />} />
        {/* Admin */}
        <Route path="/automation" element={<RequireAdmin><ConfigurationTabs>{L(<AutomationPage />)}</ConfigurationTabs></RequireAdmin>} />
        <Route path="/settings" element={<RequireAdmin><ConfigurationTabs><SettingsPage /></ConfigurationTabs></RequireAdmin>} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </ErrorBoundary>
    </AppLayout>
  );
}
